import {
  useSetTiktokVisibilityLimitOverrideMutation,
  useTiktokVisibilityLimitOverridesQuery,
} from "@/api/tiktokVisibilityApi";
import ChevronIcon from "@/assets/colorable/chevron.svg?react";
import EditIcon from "@/assets/colorable/edit.svg?react";
import InfinityIcon from "@/assets/colorable/infinity.svg?react";
import TickIcon from "@/assets/colorable/tick.svg?react";
import TrashIcon from "@/assets/colorable/trash.svg?react";
import Button from "@/components/Common/Button";
import ReusableDropdown, {type TDropdownOption} from "@/components/Common/forms/ReusableDropdown";
import Toggle from "@/components/Common/forms/Toggle";
import clsxm from "@/lib/clsxm";
import TikTokVisibilityLimitFields from "@/pages/plans/TikTokVisibilityLimitFields";
import usePackageAdminStore from "@/store/packageAdmin";
import {TIKTOK_FREQUENCY_BY_HOURS, TIKTOK_KEYWORD_LIMIT_INFINITY} from "@/utils/planUtils";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const TikTokSearchConfig: FC<{
  agencyId?: number;
  hasSubBrands?: boolean;
  isAgency: boolean;
}> = ({agencyId, hasSubBrands, isAgency}) => {
  const {t} = useTranslation(["common", "plans"]);
  const [selectedSubbrandId, setSelectedSubbrandId] = useState<number | null>(null);
  const [subbrandCountryLimitInput, setSubbrandCountryLimitInput] = useState<number | null>();
  const [subbrandKeywordLimitInput, setSubbrandKeywordLimitInput] = useState<number | null>();
  const [subbrandSyncIntervalInput, setSubbrandSyncIntervalInput] = useState<number | null>();
  const [subbrandTiktokSearchInput, setSubbrandTiktokSearchInput] = useState(false);
  const [subbrandOverridesEnabled, setSubbrandOverridesEnabled] = useState(false);

  const {
    changeValue,
    modifiedPackage: {
      tiktok_search,
      tiktok_visibility_country_count,
      tiktok_visibility_keyword_limit,
      tiktok_visibility_sync_interval_hours,
    },
  } = usePackageAdminStore();

  const {data: limitOverrides} = useTiktokVisibilityLimitOverridesQuery({
    agencyId: Number(agencyId),
    queryOptions: {enabled: isAgency && !!hasSubBrands && !!agencyId},
  });

  const subbrandOverrides = limitOverrides?.results ?? [];
  const subbrandOptions: TDropdownOption[] = subbrandOverrides.map(row => ({
    value: String(row.company_profile_id),
    label: row.brand_name,
    icon: row.picture320 ?? undefined,
  }));

  const selectedSubbrandRow = subbrandOverrides.find(row => row.company_profile_id === selectedSubbrandId);
  const limitOverrideMutation = useSetTiktokVisibilityLimitOverrideMutation({agencyId: Number(agencyId)});

  const hasActiveOverrides = subbrandOverrides.some(
    row =>
      row.tiktok_search === true ||
      row.keyword_limit != null ||
      row.sync_interval_hours != null ||
      row.country_count !== null,
  );

  const showSubbrandControls = isAgency && !!hasSubBrands;
  const showSubbrandOverrides = subbrandOverridesEnabled || hasActiveOverrides;

  return (
    <>
      {tiktok_search && (
        <div className="flex flex-wrap gap-5 lg:flex-nowrap">
          <TikTokVisibilityLimitFields
            countryCount={tiktok_visibility_country_count}
            keywordLimit={tiktok_visibility_keyword_limit}
            syncInterval={tiktok_visibility_sync_interval_hours}
            onCountryCountChange={value => changeValue("tiktok_visibility_country_count", value)}
            onKeywordLimitChange={value => changeValue("tiktok_visibility_keyword_limit", value)}
            onSyncIntervalChange={value => changeValue("tiktok_visibility_sync_interval_hours", value)}
          />
        </div>
      )}
      {showSubbrandControls && (
        <Toggle
          checked={showSubbrandOverrides}
          disabled={hasActiveOverrides}
          label={t("subbrand_tiktok_search")}
          onChange={setSubbrandOverridesEnabled}
        />
      )}
      {showSubbrandControls && showSubbrandOverrides && (
        <div className="border-neutral-grey-light flex flex-col gap-3 rounded-lg border border-dashed p-4">
          <div className="flex flex-col gap-0.5">
            <span className="text-neutral-black text-xs font-semibold">
              {t("subbrand_tiktok_visibility_override_title")}
            </span>
            <span className="text-neutral-grey-dark text-2xs">
              {t("subbrand_tiktok_visibility_override_description")}
            </span>
          </div>
          <ReusableDropdown
            isSearchable
            label={t("select_subbrand")}
            containerClassName="w-full lg:max-w-sm"
            setSelectedOption={option => {
              const id = +option.value;
              setSelectedSubbrandId(id);
              const subbrandRow = subbrandOverrides.find(row => row.company_profile_id === id);
              setSubbrandTiktokSearchInput(subbrandRow?.tiktok_search ?? false);
              setSubbrandCountryLimitInput(subbrandRow?.country_count ?? subbrandRow?.effective_country_count);
              setSubbrandKeywordLimitInput(subbrandRow?.keyword_limit ?? subbrandRow?.effective_keyword_limit);
              setSubbrandSyncIntervalInput(
                subbrandRow?.sync_interval_hours ?? subbrandRow?.effective_sync_interval_hours,
              );
            }}
            selectedOption={subbrandOptions.find(option => +option.value === selectedSubbrandId) ?? null}
            options={subbrandOptions}
            labelClassName="text-neutral-black text-xs font-normal"
            renderButton={({selectedOption, isOpen, className, ...rest}) => (
              <button
                {...rest}
                className={clsxm(
                  className,
                  isOpen ? "border-primary-medium-solid" : "border-neutral-grey-light",
                  selectedOption ? "text-neutral-black font-medium" : "text-neutral-grey-light",
                  "h-10 gap-2 rounded-md pl-3",
                )}>
                {selectedOption?.icon && (
                  <img src={selectedOption.icon} className="size-6 shrink-0 rounded-full object-cover" />
                )}
                <span className="grow items-center truncate text-left">
                  {selectedOption?.label ?? t("select_subbrand")}
                </span>
                <ChevronIcon
                  className={clsxm(
                    "text-primary-dark-solid mr-4 transition-transform duration-250",
                    isOpen ? "rotate-180" : "rotate-0",
                  )}
                />
              </button>
            )}
            renderItem={({option, isHighlighted, isSelected, className, ...rest}) => (
              <li
                {...rest}
                value={option.value}
                className={clsxm(
                  className,
                  isHighlighted ? "bg-primary-light-solid" : "hover:bg-neutral-white-dark",
                  "h-10 gap-2",
                )}>
                {option.icon ? (
                  <img src={option.icon} className="size-6 shrink-0 rounded-full object-cover" />
                ) : (
                  <div className="bg-neutral-grey-lightest size-6 shrink-0 rounded-full" />
                )}
                <span className="grow truncate leading-none">{option.label}</span>
                {isSelected && <TickIcon className="text-primary-dark-solid" />}
              </li>
            )}
          />
          {selectedSubbrandId && (
            <>
              <Toggle
                checked={subbrandTiktokSearchInput}
                label={t("grant_tiktok_search")}
                onChange={setSubbrandTiktokSearchInput}
              />
              <div className="flex flex-wrap items-end gap-4 lg:flex-nowrap">
                <TikTokVisibilityLimitFields
                  isDisabled={!selectedSubbrandId}
                  countryCount={subbrandCountryLimitInput}
                  keywordLimit={subbrandKeywordLimitInput}
                  syncInterval={subbrandSyncIntervalInput}
                  onCountryCountChange={setSubbrandCountryLimitInput}
                  onKeywordLimitChange={setSubbrandKeywordLimitInput}
                  onSyncIntervalChange={setSubbrandSyncIntervalInput}
                />
                <Button
                  buttonStyle="Blue"
                  className="h-10 shrink-0 px-5"
                  isDisabled={
                    selectedSubbrandId === null ||
                    (subbrandCountryLimitInput ==
                      (selectedSubbrandRow?.country_count ?? selectedSubbrandRow?.effective_country_count) &&
                      subbrandKeywordLimitInput ==
                        (selectedSubbrandRow?.keyword_limit ?? selectedSubbrandRow?.effective_keyword_limit) &&
                      subbrandSyncIntervalInput ==
                        (selectedSubbrandRow?.sync_interval_hours ??
                          selectedSubbrandRow?.effective_sync_interval_hours) &&
                      subbrandTiktokSearchInput === (selectedSubbrandRow?.tiktok_search ?? false))
                  }
                  isLoading={
                    limitOverrideMutation.isPending &&
                    limitOverrideMutation.variables?.companyProfileId === selectedSubbrandId
                  }
                  onClick={() => {
                    if (selectedSubbrandId === null) return;
                    limitOverrideMutation.mutate({
                      companyProfileId: selectedSubbrandId,
                      tiktokSearch: subbrandTiktokSearchInput,
                      countryCount: subbrandCountryLimitInput != null ? Number(subbrandCountryLimitInput) : null,
                      keywordLimit: subbrandKeywordLimitInput != null ? Number(subbrandKeywordLimitInput) : null,
                      syncIntervalHours: subbrandSyncIntervalInput != null ? Number(subbrandSyncIntervalInput) : null,
                    });
                  }}>
                  {t("Save")}
                </Button>
              </div>
            </>
          )}
          {hasActiveOverrides && (
            <>
              <div className="bg-neutral-grey-light h-px w-full" />
              <span className="text-neutral-black text-xs font-semibold">{t("active_overrides")}</span>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="text-neutral-grey-dark text-2xs font-normal">
                      <th className="py-2 pr-3 font-normal">{t("plans:subbrand")}</th>
                      <th className="px-3 py-2 text-center font-normal">{t("tiktok_search")}</th>
                      <th className="px-3 py-2 font-normal">{t("tiktok_keyword_limit")}</th>
                      <th className="px-3 py-2 font-normal">{t("tiktok_sync_interval_hours")}</th>
                      <th className="px-3 py-2 font-normal">{t("tiktok_country_limit")}</th>
                      <th className="py-2 pl-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-neutral-grey-light divide-y">
                    {subbrandOverrides
                      .filter(
                        row =>
                          row.tiktok_search === true ||
                          row.keyword_limit != null ||
                          row.sync_interval_hours != null ||
                          row.country_count !== null,
                      )
                      .map(row => {
                        const isRemoving =
                          limitOverrideMutation.isPending &&
                          limitOverrideMutation.variables?.companyProfileId === row.company_profile_id;
                        const dash = <span className="text-neutral-grey-light">—</span>;

                        return (
                          <tr className="text-neutral-black text-sm" key={row.company_profile_id}>
                            <td className="py-2 pr-3">
                              <div className="flex items-center gap-2">
                                {row.picture320 ? (
                                  <img src={row.picture320} className="size-7 shrink-0 rounded-full object-cover" />
                                ) : (
                                  <div className="bg-neutral-grey-lightest size-7 shrink-0 rounded-full" />
                                )}
                                <span className="min-w-0 truncate font-medium">{row.brand_name}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {row.tiktok_search === true ? (
                                <TickIcon className="text-primary-dark-solid mx-auto size-4" />
                              ) : (
                                dash
                              )}
                            </td>
                            <td className="px-3 py-2 font-semibold">
                              {row.keyword_limit != null ? (
                                row.keyword_limit >= TIKTOK_KEYWORD_LIMIT_INFINITY ? (
                                  <InfinityIcon className="w-4" />
                                ) : (
                                  row.keyword_limit
                                )
                              ) : (
                                dash
                              )}
                            </td>
                            <td className="px-3 py-2 font-semibold">
                              {row.sync_interval_hours != null
                                ? t(
                                    TIKTOK_FREQUENCY_BY_HOURS[row.sync_interval_hours]
                                      ?.word as "tiktok_visibility_frequency_daily",
                                  )
                                : dash}
                            </td>
                            <td className="px-3 py-2 font-semibold">
                              {row.country_count !== null ? row.country_count : dash}
                            </td>
                            <td className="py-2 pl-3">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  buttonStyle="Custom"
                                  className="text-neutral-grey-dark hover:bg-neutral-white-dark flex size-8 shrink-0 items-center justify-center rounded-md p-0"
                                  isDisabled={isRemoving}
                                  onClick={() => {
                                    setSelectedSubbrandId(row.company_profile_id);
                                    setSubbrandCountryLimitInput(row.country_count ?? row.effective_country_count);
                                    setSubbrandKeywordLimitInput(row.keyword_limit ?? row.effective_keyword_limit);
                                    setSubbrandSyncIntervalInput(
                                      row.sync_interval_hours ?? row.effective_sync_interval_hours,
                                    );
                                    setSubbrandTiktokSearchInput(row.tiktok_search ?? false);
                                  }}>
                                  <EditIcon className="size-4" />
                                </Button>
                                <Button
                                  buttonStyle="Custom"
                                  className="text-danger-medium-red hover:bg-danger-light-red flex size-8 shrink-0 items-center justify-center rounded-md p-0"
                                  isLoading={isRemoving}
                                  onClick={() =>
                                    limitOverrideMutation.mutate({
                                      companyProfileId: row.company_profile_id,
                                      countryCount: null,
                                      keywordLimit: null,
                                      syncIntervalHours: null,
                                      tiktokSearch: false,
                                    })
                                  }>
                                  <TrashIcon className="size-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default TikTokSearchConfig;
